import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import RelatedPages from '@/components/base/RelatedPages';
import ServiceSubNav from '@/components/base/ServiceSubNav';

const SERVICE_SUBNAV_ITEMS = [
  { label: '熱銷配方', path: '/services/hot-formulas' },
  { label: '天然原料', path: '/services/natural-ingredients' },
  { label: '各類劑型', path: '/services/dosage-forms' },
  { label: '包裝設計', path: '/services/packaging' },
];

const processSteps = [
  { num: '01', icon: 'ri-discuss-line', title: '需求對焦', desc: '確認品牌風格、目標族群、通路需求與包裝規格方向。' },
  { num: '02', icon: 'ri-palette-line', title: '初稿製作', desc: '設計師依照需求提供 2–3 套包裝視覺草稿供選擇。' },
  { num: '03', icon: 'ri-printer-line', title: '樣品印刷', desc: '確認設計稿後製作印刷打樣，實際確認色彩與質感。' },
  { num: '04', icon: 'ri-edit-line', title: '調整設計', desc: '依打樣結果修正色差、字體與版面細節。' },
  { num: '05', icon: 'ri-search-eye-line', title: '完稿校稿', desc: '法規標示內容最終審查，確保成分、標示格式符合規範。' },
  { num: '06', icon: 'ri-send-plane-line', title: '送出印製', desc: '完稿定版後送印製廠量產，依排程準時交貨。' },
];

const packagingTypes = [
  {
    id: 'box',
    icon: 'ri-archive-line',
    title: '各類盒型',
    desc: '外盒是產品品牌形象的第一印象。康永提供多種盒型選擇，從一般掀蓋盒、飛機盒到抽屜式盒型 ，可依產品定位與售價帶選擇最適合的外盒設計。',
    materials: '一般白紙盒、特殊紙材盒(瓦楞紙、牛皮紙、銀箔紙)',
    suitable: ['膠囊/鋁包外盒', '瓶裝外盒', '禮盒', '高端品牌包裝'],
    img: 'https://readdy.ai/api/search-image?query=premium%20health%20supplement%20product%20box%20packaging%20cardboard%20white%20minimal%20clean%20design%20elegant%20brand%20packaging%20mockup%20on%20white%20background%20professional%20photography&width=600&height=380&seq=pkg-box-01&orientation=landscape',
    extraSections: [
      { title: '盒型款式', content: '簡單上掀盒、上掀日本底、上掀糊底盒、飛機盒、抽屜盒、吊盒、紙圓罐、天地盒、精裝盒' },
      { title: '常見後加工', content: '霧膜局亮、亮膜、打凸、打凹、燙金、燙銀' },
      { title: '注意事項', content: '單款印刷MOQ: 500 盒' },
    ],
  },
  {
    id: 'bottle',
    icon: 'ri-flask-line',
    title: '瓶罐類型',
    desc: '瓶罐是膠囊、錠劑、粉末產品最常見的主包材。依原料特性可選擇 HDPE 塑膠瓶、PET 透明瓶、玻璃瓶或鋁罐，不同材質在防潮、遮光與質感上各有優勢。',
    materials: 'HDPE 塑膠瓶（白色 / 有色）、PET 透明瓶、玻璃瓶、鋁罐',
    suitable: ['膠囊 / 錠劑裝填', '蛋白質粉末', '高端精品配方', '素食認證產品'],
    img: 'https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/e76dfb7f242a82353ec044233d8d22ed.jpeg',
    extraSections: [
      { title: '瓶罐款式', content: '廣口瓶、透明瓶、茶色瓶、掀蓋瓶、旋轉蓋、撕拉蓋、亮銀選轉蓋、亮金旋轉蓋、兒童安全蓋' },
      { title: '常見包裝', content: '罐貼、瓶蓋貼' },
      { title: '注意事項', content: '單款 MOQ ：依訂單量評估' },
    ],
  },
  {
    id: 'foil',
    icon: 'ri-file-paper-2-line',
    title: '鋁箔包裝',
    desc: '鋁箔立袋或鋁箔條狀包是粉末、顆粒與果凍類產品的主要包材選擇。鋁箔材質提供優異的防潮、遮光效果，特別適合益生菌等對環境敏感的原料。',
    materials: '鋁箔立袋（夾鏈 / 無夾鏈）、鋁箔條狀包（分包機充填）',
    suitable: ['粉末補充品', '益生菌配方', '果凍條', '沖泡茶品'],
    img: 'https://readdy.ai/api/search-image?query=aluminum%20foil%20pouch%20sachet%20packaging%20for%20health%20supplement%20powder%20probiotic%20on%20clean%20white%20background%2C%20metallic%20silver%20flexible%20packaging%20product%20photography&width=600&height=380&seq=pkg-foil-01&orientation=landscape',
    extraSections: [
      { title: '鋁袋款式', content: '三面封、背封、水平封、夾鏈式站立袋' },
      { title: '常見', content: '罐貼、瓶蓋貼' },
      { title: '注意事項', content: '單款 MOQ ：依訂單量評估；鋁袋設計樣式：可使用 客供 或 公版' },
    ],
  },
  {
    id: 'ptp',
    icon: 'ri-layout-grid-line',
    title: 'PTP 包裝',
    desc: 'PTP(Press Through Package) 是錠劑與膠囊的高端包材選擇 。 透過泡殼包裝將 每粒分格密封保存，防潮性極佳，適合高單價品牌或訴求高端質感的保健食品，常見於日本與歐洲市場產品。',
    materials: 'PVC / PVDC泡殼 + 鋁箔背膜，鋁箔背膜可客製印刷樣式或使用公版款',
    suitable: ['軟硬膠囊', '錠劑', '質感包裝產品'],
    img: 'https://readdy.ai/api/search-image?query=PTP%20blister%20pack%20pharmaceutical%20supplement%20capsule%20tablet%20blister%20packaging%20silver%20aluminum%20press%20through%20package%20on%20white%20background%2C%20high%20end%20health%20product%20packaging%20photography&width=600&height=380&seq=pkg-ptp-01&orientation=landscape',
    extraSections: [
      { title: '鋁袋款式', content: '三面封、背封、水平封、夾鏈式站立袋' },
      { title: '常見加工', content: '客供設計、公版(紅手壓 藍手壓)' },
      { title: '注意事項', content: '單款MOQ ：依訂單量評估；鋁箔背膜設計樣式：可使用 客供 或 公版' },
    ],
  },
];

export default function PackagingContent() {
  const [openDetail, setOpenDetail] = useState<string | null>(null);

  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20health%20supplement%20product%20packaging%20design%20collection%20various%20boxes%20bottles%20pouches%20on%20clean%20white%20background%2C%20professional%20brand%20packaging%20display%20photography%20minimal&width=1920&height=420&seq=pkg-hero-bg-01&orientation=landscape"
            alt="包裝設計背景"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/65 to-[#1a3a1a]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb
              items={[
                { label: '首頁', href: '/' },
                { label: '代工服務', href: '/services' },
                { label: '包裝設計' },
              ]}
              className="mb-4 text-gray-300"
            />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              代工服務
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              包裝設計
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              從包材選擇、視覺設計到法規標示，協助品牌打造完整且具辨識度的保健產品包裝。
            </p>
          </div>
        </div>
      </section>

      {/* Design process */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              設計流程
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              包裝設計六步驟
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              從需求對焦到送出印製，每個步驟均有專業人員把關，確保最終包裝完美呈現
            </p>
          </div>

          {/* Desktop horizontal flow */}
          <div className="hidden lg:grid grid-cols-6 gap-0 mb-10">
            {processSteps.map((s, idx) => (
              <div key={s.num} className="relative flex flex-col items-center">
                {idx < processSteps.length - 1 && (
                  <div className="absolute top-9 left-1/2 w-full h-0.5 bg-green-200 z-0" />
                )}
                <div className="relative z-10 flex flex-col items-center px-3 w-full">
                  <div className="w-[72px] h-[72px] flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white mb-4 relative">
                    <i className={`${s.icon} text-[#4CAF50] text-2xl`} />
                    <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-[#4CAF50] text-white text-[0.625rem] font-bold rounded-full">
                      {s.num}
                    </span>
                  </div>
                  <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5 text-center">{s.title}</h3>
                  <p className="text-gray-500 text-[0.8125rem] leading-[1.65] text-center">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile vertical flow */}
          <div className="lg:hidden space-y-0">
            {processSteps.map((s, idx) => (
              <div key={s.num} className="flex gap-4">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white relative flex-shrink-0">
                    <i className={`${s.icon} text-[#4CAF50] text-xl`} />
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-[#4CAF50] text-white text-[0.625rem] font-bold rounded-full">
                      {s.num}
                    </span>
                  </div>
                  {idx < processSteps.length - 1 && (
                    <div className="w-0.5 flex-1 bg-green-200 my-2 min-h-[32px]" />
                  )}
                </div>
                <div className="pb-6 flex-1 min-w-0">
                  <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-1">{s.title}</h3>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Reminder */}
          <div className="mt-10 bg-white rounded-2xl border border-green-100 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-xl flex-shrink-0 border border-green-100">
              <i className="ri-information-line text-[#4CAF50] text-xl" />
            </div>
            <div>
              <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">標示法規審查不可省略</p>
              <p className="text-gray-500 text-[0.875rem] leading-[1.75]">
                包裝完稿前，康永會協助審查成分標示、重量標示、保存方式等法定標示項目，確保上市後符合台灣食品安全衛生管理法規定。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Packaging types */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              包材類型
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">
              多元包材選擇
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              依產品定位、劑型特性與目標市場，選擇最適合的包材組合
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            {packagingTypes.map((p) => (
              <div key={p.id} className="bg-[#f7faf7] rounded-2xl overflow-hidden border border-gray-100 hover:border-green-200 transition-all duration-300">
                {/* Image */}
                <div className="w-full h-[380px] lg:h-[400px] overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.title}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                  />
                </div>
                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-white rounded-xl border border-green-100">
                      <i className={`${p.icon} text-[#4CAF50] text-lg`} />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.125rem]">{p.title}</h3>
                  </div>
                  <p className="text-gray-600 text-[0.9375rem] leading-[1.75] mb-4">{p.desc}</p>

                  {/* Materials */}
                  <div className="bg-white rounded-xl p-3.5 mb-4 border border-gray-100">
                    <p className="text-[#1a2e1a] font-semibold text-[0.8125rem] mb-1 flex items-center gap-1.5">
                      <i className="ri-tools-line text-[#4CAF50] text-sm" />
                      常見材質
                    </p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{p.materials}</p>
                  </div>

                  {/* Suitable */}
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {p.suitable.map((s) => (
                      <span key={s} className="bg-green-50 border border-green-100 text-[#4CAF50] text-[0.75rem] px-2.5 py-1 rounded-full font-medium">
                        {s}
                      </span>
                    ))}
                  </div>

                  {/* Toggle detail button */}
                  {'extraSections' in p && p.extraSections.length > 0 && (
                    <>
                      <button
                        onClick={() => setOpenDetail(openDetail === p.id ? null : p.id)}
                        className="flex items-center gap-2 text-[#4CAF50] font-medium text-[0.875rem] cursor-pointer hover:text-[#43a047] transition-colors duration-200 mb-3"
                      >
                        <i className={`${openDetail === p.id ? 'ri-subtract-line' : 'ri-add-line'} text-base`} />
                        {openDetail === p.id ? '收合包材細節' : '查看包材細節'}
                      </button>

                      {openDetail === p.id && (
                        <div className="space-y-3 mb-5 pt-4 border-t border-gray-100">
                          {p.extraSections.map((sec, idx) => (
                            <div key={idx} className="bg-white rounded-xl p-3 border border-gray-100">
                              <p className="text-[#1a2e1a] font-semibold text-[0.8125rem] mb-1 flex items-center gap-1.5">
                                <i className="ri-file-list-3-line text-[#4CAF50] text-sm" />
                                {sec.title}
                              </p>
                              <p className="text-gray-500 text-[0.8125rem] leading-[1.7] whitespace-pre-line">{sec.content}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  <Link
                    to="/contact/form"
                    className="whitespace-nowrap inline-flex items-center gap-1.5 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[0.875rem] px-5 py-2.5 rounded-full transition-all duration-200 cursor-pointer"
                  >
                    洽詢包材樣式
                    <i className="ri-arrow-right-line text-sm" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Label compliance reminder */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-10 items-center">
            <div className="w-full lg:w-1/2">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                標示合規
              </span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.625rem] leading-tight mb-5">
                包裝不只是美觀，<br />標示合規同樣重要
              </h2>
              <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">
                台灣食品標示法規明確要求包裝上必須標示品名、成分、添加物、重量、有效日期、保存方式與廠商資訊等，違規標示可能導致產品被要求下架或遭罰鍰。
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[400px]">
                  <thead>
                    <tr>
                      <th colSpan={2} className="bg-gray-100 border border-gray-200 px-4 py-3.5 text-center text-[#1a2e1a] font-bold text-[0.9375rem]">
                        標示項目列點
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">品名、品牌名稱、識別標示</td>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">公司名稱、地址、電話</td>
                    </tr>
                    <tr>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">成分標示（依含量由高至低排序）</td>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">原產地</td>
                    </tr>
                    <tr>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">淨重 / 容量標示</td>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">營養標示</td>
                    </tr>
                    <tr>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">保存期限 / 保存方式</td>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">回收標誌 / 食品標誌</td>
                    </tr>
                    <tr>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">有效期限</td>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">產品保險資訊</td>
                    </tr>
                    <tr>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">注意事項 / 成分警語</td>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">素食標示</td>
                    </tr>
                    <tr>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">過敏原資訊</td>
                      <td className="w-1/2 border border-gray-200 px-4 py-3 text-center text-gray-800 text-[0.875rem]">合法的食品廣告用詞</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=health%20supplement%20product%20label%20design%20packaging%20close%20up%20nutrition%20facts%20ingredient%20list%20regulatory%20compliance%20label%20printing%20on%20clean%20white%20bottle%2C%20professional%20product%20label%20photography&width=720&height=460&seq=pkg-label-01&orientation=landscape"
                  alt="包裝標示合規"
                  className="w-full h-[320px] lg:h-[380px] object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            讓康永協助您打造最適合的包裝方案
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            從包材選擇到標示審查，一站式解決您的包裝設計需求，讓產品在貨架上脫穎而出。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact/form"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              洽詢包裝方案
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/cooperation/process"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              了解合作流程
            </Link>
          </div>
        </div>
      </section>
      <ServiceSubNav
        items={SERVICE_SUBNAV_ITEMS}
        backLabel="返回服務總覽"
        backPath="/services"
        sectionLabel="其他服務頁面"
      />
      <RelatedPages items={[
        { title: '各類劑型', desc: '確認劑型後，選擇最搭配的包材組合完整呈現品牌形象。', href: '/services/dosage-forms' },
        { title: '法規風險', desc: '包裝標示的法規要求，讓康永協助您確保每個細節合規。', href: '/about/regulation' },
        { title: '立即洽詢', desc: '填寫諮詢表單，讓康永協助您打造最適合的包裝設計。', href: '/contact/form' },
      ]} />
    </>
  );
}
